/**
 * Annotation table — "filter by specific values" checkbox list
 *
 * FLOW (AnnotationTablePage `columnFilters` state):
 * 1. User opens column filter popover → unique values for that column are passed in as `uniqueColumnValues`.
 * 2. Base input (search / number comparison) lives in AnnotationTablePage or TextColumnWithCheckboxListFilter.
 * 3. ColumnFilterCheckboxListPanel renders the checkbox list (this file).
 * 4. User checks "All" or individual values → setColumnFilterAllValues / setColumnFilterOneValue
 *    update columnFilters via setColumnFilters.
 * 5. filteredAnnotations (AnnotationTablePage) reads the same flags/sets to include or exclude rows.
 *
 * Stored flags (same meaning for every filter type):
 * - excludeAllColumnValues: true → user selected no values in the list → no row matches this column.
 * - allowedValues (Set): when non-empty, only rows whose cell value is in this set pass (number/text/checkbox).
 */

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

const DEFAULT_NUMBER_FILTER = {
  operator: '>',
  compareValue: '',
  allowedValues: new Set(),
  excludeAllColumnValues: false,
};

/**
 * Number-column filter state for the popover (comparison operator + threshold + value checkbox list).
 * Used in AnnotationTablePage when the user edits the operator dropdown or the threshold field,
 * and inside setColumnFilter* when the checkbox list changes — keeps one shape with safe defaults.
 */
export function readNumberColumnFilterState(columnFilterEntry) {
  if (columnFilterEntry?.type !== 'number') {
    return { ...DEFAULT_NUMBER_FILTER, allowedValues: new Set() };
  }
  const stored = columnFilterEntry.value || {};
  return {
    operator: stored.operator || '>',
    compareValue: stored.compareValue ?? stored.value ?? '',
    allowedValues: stored.allowedValues
      ? new Set(stored.allowedValues)
      : stored.size
        ? new Set(stored.size)
        : new Set(),
    excludeAllColumnValues: !!(
      stored.excludeAllColumnValues ?? stored.discreteNone
    ),
  };
}

/**
 * User toggled the master "All" checkbox in the value list.
 * filterType matches columnFilters[].type: 'number' | 'checkbox' | 'text'.
 */
export function setColumnFilterAllValues(prev, columnKey, filterType, allChecked) {
  if (filterType === 'number') {
    const numberPart = readNumberColumnFilterState(prev[columnKey]);
    return {
      ...prev,
      [columnKey]: {
        type: 'number',
        value: {
          ...numberPart,
          allowedValues: new Set(),
          excludeAllColumnValues: !allChecked,
        },
      },
    };
  }

  if (filterType === 'checkbox') {
    if (allChecked) {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    }
    return {
      ...prev,
      [columnKey]: {
        type: 'checkbox',
        allowedValues: new Set(),
        excludeAllColumnValues: true,
      },
    };
  }

  const textEntry = prev[columnKey];
  const searchText = textEntry?.type === 'text' ? (textEntry.value || '') : '';
  if (allChecked) {
    if (
      !searchText.trim() &&
      !textEntry?.allowedValues?.size &&
      !textEntry?.valueSet?.size &&
      !textEntry?.excludeAllColumnValues &&
      !textEntry?.emptyDiscrete
    ) {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    }
    return {
      ...prev,
      [columnKey]: { type: 'text', value: searchText, excludeAllColumnValues: false },
    };
  }
  return {
    ...prev,
    [columnKey]: {
      type: 'text',
      value: searchText,
      allowedValues: new Set(),
      excludeAllColumnValues: true,
    },
  };
}

/**
 * User toggled one value in the checkbox list.
 */
export function setColumnFilterOneValue(
  prev,
  columnKey,
  filterType,
  cellValue,
  checked,
  uniqueColumnValues
) {
  const valueKey = String(cellValue);

  if (filterType === 'number') {
    const numberPart = readNumberColumnFilterState(prev[columnKey]);
    const allowed = numberPart.excludeAllColumnValues
      ? new Set()
      : numberPart.allowedValues.size > 0
        ? new Set(numberPart.allowedValues)
        : new Set(uniqueColumnValues.map(String));
    if (checked) allowed.add(valueKey);
    else allowed.delete(valueKey);

    const base = {
      operator: numberPart.operator,
      compareValue: numberPart.compareValue,
    };
    if (allowed.size === 0) {
      return {
        ...prev,
        [columnKey]: {
          type: 'number',
          value: { ...base, allowedValues: new Set(), excludeAllColumnValues: true },
        },
      };
    }
    if (allowed.size === uniqueColumnValues.length) {
      return {
        ...prev,
        [columnKey]: {
          type: 'number',
          value: { ...base, allowedValues: new Set(), excludeAllColumnValues: false },
        },
      };
    }
    return {
      ...prev,
      [columnKey]: {
        type: 'number',
        value: { ...base, allowedValues: allowed, excludeAllColumnValues: false },
      },
    };
  }

  if (filterType === 'checkbox') {
    const entry = prev[columnKey];
    const allowed =
      entry?.type === 'checkbox'
        ? new Set(
            entry.excludeAllColumnValues || entry.emptyDiscrete
              ? []
              : entry.allowedValues || entry.value || []
          )
        : new Set(uniqueColumnValues.map(String));
    if (checked) allowed.add(valueKey);
    else allowed.delete(valueKey);

    if (allowed.size === 0) {
      return {
        ...prev,
        [columnKey]: {
          type: 'checkbox',
          allowedValues: new Set(),
          excludeAllColumnValues: true,
        },
      };
    }
    if (allowed.size === uniqueColumnValues.length) {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    }
    return {
      ...prev,
      [columnKey]: {
        type: 'checkbox',
        allowedValues: allowed,
        excludeAllColumnValues: false,
      },
    };
  }

  const textEntry = prev[columnKey];
  const searchText = textEntry?.type === 'text' ? (textEntry.value || '') : '';
  const allowed =
    textEntry?.type === 'text'
      ? textEntry.excludeAllColumnValues || textEntry.emptyDiscrete
        ? new Set()
        : textEntry.allowedValues?.size > 0
          ? new Set(textEntry.allowedValues)
          : textEntry.valueSet?.size > 0
            ? new Set(textEntry.valueSet)
            : new Set(uniqueColumnValues.map(String))
      : new Set(uniqueColumnValues.map(String));
  if (checked) allowed.add(valueKey);
  else allowed.delete(valueKey);

  if (allowed.size === 0) {
    return {
      ...prev,
      [columnKey]: {
        type: 'text',
        value: searchText,
        allowedValues: new Set(),
        excludeAllColumnValues: true,
      },
    };
  }
  if (allowed.size === uniqueColumnValues.length) {
    if (!searchText.trim()) {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    }
    return {
      ...prev,
      [columnKey]: { type: 'text', value: searchText, excludeAllColumnValues: false },
    };
  }
  return {
    ...prev,
    [columnKey]: {
      type: 'text',
      value: searchText,
      allowedValues: allowed,
      excludeAllColumnValues: false,
    },
  };
}

/** Renders the checkbox list block inside the column filter popover. */
export function ColumnFilterCheckboxListPanel({
  uniqueColumnValues,
  allChecked,
  indeterminate,
  onAllChange,
  getIsChecked,
  onRowToggle,
}) {
  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
      <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
        Or filter by specific values:
      </Typography>
      {uniqueColumnValues.length > 0 && (
        <FormControlLabel
          sx={{ display: 'flex', mb: 0.5, alignItems: 'center' }}
          control={
            <Checkbox
              size="small"
              checked={allChecked}
              indeterminate={indeterminate}
              onChange={onAllChange}
            />
          }
          label={<Typography variant="body2">All</Typography>}
        />
      )}
      <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
        {uniqueColumnValues.length > 0 ? (
          uniqueColumnValues.map((val) => (
            <Box key={String(val)} sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
              <Checkbox
                size="small"
                checked={getIsChecked(val)}
                onChange={(e) => onRowToggle(val, e.target.checked)}
              />
              <Typography variant="body2">{val}</Typography>
            </Box>
          ))
        ) : (
          <Typography variant="body2" sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
            Calculating values...
          </Typography>
        )}
      </Box>
    </Box>
  );
}
